import React, { ReactNode, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { format } from "date-fns";
import { LayoutDashboardIcon, Loader2Icon, PlayIcon } from "lucide-react";
import type { OnboardingAnswers, Placement, StepId } from "../../../types/onboarding";
import { goalOptions, minuteOptions, reminderOptions, skillOptions } from "../../../data/onboarding";
import { units } from "../../../data/courses";
import { PairChips } from "../../ui/LangChip";
import { BrutalButton } from "../../ui/BrutalButton";
import { LevelMeter } from "../LevelMeter";
import { courseTitle, getLanguage } from "../../../utils/language";
import { projectNextLevel, selfOnlyPlacement } from "../../../utils/placement";
import { spring } from "../../../utils/motion";
interface PlanStepProps {
  answers: OnboardingAnswers;
  placement: Placement;
  onEdit: (step: StepId) => void;
  onFinish: (dest: "lesson" | "dashboard") => void;
}
export function PlanStep({
  answers,
  placement,
  onEdit,
  onFinish
}: PlanStepProps) {
  const reduce = useReducedMotion() ?? false;
  // A short, honest "thinking" beat (under a second) so the result reads as computed, not canned.
  const [ready, setReady] = useState(reduce);
  useEffect(() => {
    if (ready) return;
    const t = window.setTimeout(() => setReady(true), 850);
    return () => window.clearTimeout(t);
  }, [ready]);
  const target = answers.target ?? "es";
  const unit = units[placement.unit - 1] ?? units[0];
  const firstLesson = unit.lessons[0];
  const projection = projectNextLevel(placement, answers.minutes);
  const minutes = minuteOptions.find(m => m.minutes === answers.minutes);
  const reminder = reminderOptions.find(r => r.id === answers.reminder);
  const skipped = placement.unit > 1 ? (placement.unit - 1) * 5 : 0;
  return <>
      {!ready ? <motion.div key="building" className="flex min-h-[50vh] flex-col items-center justify-center text-center" role="status" data-id="element-1007" data-mp="wchj7y4wo8gn">
          <Loader2Icon className="h-8 w-8 animate-spin text-brand" aria-hidden="true" data-id="element-1008" data-mp="yhe62b6fhm2v" />
          <p className="mt-4 text-lg font-bold text-ink" data-id="element-1009" data-mp="13enxfwddo7tc">Building your plan…</p>
        </motion.div> : <motion.div key="plan" initial={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={spring.smooth} data-id="element-1010" data-mp="18dquatj7f7q8">
          <div className="mb-7" data-id="element-1011" data-mp="1i4c319sldsmv">
            <PairChips target={target} base={answers.base} size="md" data-id="element-1012" data-mp="1i53m5l505xw9" />
            <h1 className="mt-4 text-[30px] font-bold leading-[1.08] tracking-[-0.02em] text-ink md:text-[44px]" data-id="element-1013" data-mp="1m5xdyplkfwuf">Your plan is ready.</h1>
            <p className="mt-3 max-w-[56ch] text-[15px] leading-relaxed text-ink-2 md:text-base" data-id="element-1014" data-mp="1r61u2eikapk4">
              {courseTitle({
            target,
            base: answers.base
          })}, starting where you actually are.
              {skipped > 0 && ` You'll skip ${skipped} lessons you don't need.`}
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]" data-id="element-1015" data-mp="1qvbantlhd0g">
            {/* The one thing that matters now: where you start, and the button to start */}
            <section aria-labelledby="start-title" className="tex-ink-dots rounded-panel border-3 border-line bg-brand-tint p-6 shadow-brutal md:p-7" data-id="element-1016" data-mp="3lkyuklx6coj3">
              <p className="text-sm font-semibold text-ink-2" data-id="element-1017" data-mp="3gk1fwjbdicmc">
                Starting point · {placement.label}
                {placement.checked ? " · checked" : ""}
              </p>
              <h2 id="start-title" className="mt-1 text-[28px] font-bold leading-tight tracking-[-0.02em] text-ink md:text-4xl" data-id="element-1018" data-mp="d4nk5h3nlaxa">
                Unit {unit.number}: {unit.title}
              </h2>
              <p className="mt-2 text-[15px] text-ink-2" data-id="element-1019" data-mp="35ejk5ea8y1y">{unit.description}</p>
              <div className="mt-6 max-w-sm rounded-card bg-surface/80 p-4" data-id="element-1020" data-mp="3ucr0qkf61bhv">
                <LevelMeter band={placement.band} data-id="element-1021" data-mp="2nxqg3avlcfql" />
                <p className="mt-3 text-sm text-ink-2" data-id="element-1022" data-mp="2nvdpk9brox1x">
                  {projection.next} around <strong className="text-ink" data-id="element-1023" data-mp="2iwu387t9hj91">{format(projection.date, "MMMM yyyy")}</strong> at {answers.minutes} min a day.
                </p>
              </div>
              <div className="mt-7 flex flex-wrap items-center gap-4" data-id="element-1024" data-mp="ge5alobysj0s">
                <BrutalButton size="lg" onClick={() => onFinish("lesson")} data-id="element-1025" data-mp="1p0uccoqcovb">
                  <PlayIcon className="h-5 w-5 fill-current" aria-hidden="true" data-id="element-1026" data-mp="1mwfizpam5g9" /> Start “{firstLesson.title}”
                </BrutalButton>
                <button type="button" onClick={() => onFinish("dashboard")} className="focus-ring flex items-center gap-2 rounded-lg px-1 py-2 text-[15px] font-semibold text-ink underline decoration-ink/30 underline-offset-4 hover:decoration-ink" data-id="element-1027" data-mp="ud51l2my9a04">
                  <LayoutDashboardIcon className="h-4 w-4" aria-hidden="true" data-id="element-1028" data-mp="pdk1upzygxmc" /> Go to dashboard
                </button>
              </div>
            </section>

            {/* Everything else, reviewable and editable in place */}
            <section aria-label="Plan details" className="rounded-panel border-3 border-line/40 bg-surface p-5" data-id="element-1029" data-mp="3a63zyr7xry6v">
              <dl className="divide-y divide-line/10" data-id="element-1030" data-mp="1s90kjr015cg8">
                {answers.targets.length > 1 && <Detail label="Also learning" onEdit={() => onEdit("languages")} data-id="element-1031" data-mp="217vrj5aweqkl">
                    {answers.targets.slice(1).map(code => `${getLanguage(code).name} · Unit ${selfOnlyPlacement(answers, code).unit}`).join(", ")}
                  </Detail>}
                <Detail label="Why" onEdit={() => onEdit("goals")} data-id="element-1032" data-mp="14io41rqpy7e1">
                  {answers.goals.map(g => goalOptions.find(o => o.id === g)?.label).join(", ")}
                </Detail>
                <Detail label="Level" onEdit={() => onEdit("level")} data-id="element-1033" data-mp="1dh8i2flc3svx">
                  {placement.level} · {placement.label}
                </Detail>
                <Detail label="Extra practice" onEdit={() => onEdit("focus")} data-id="element-1034" data-mp="pqchasywhncj">
                  {answers.focus.map(f => skillOptions.find(s => s.key === f)?.label).join(", ")}
                  {!answers.speaking && " · no speaking exercises"}
                </Detail>
                <Detail label="Daily goal" onEdit={() => onEdit("schedule")} data-id="element-1035" data-mp="2ptdm3p7lzjkp">
                  {answers.minutes} min · {minutes?.xp ?? 40} XP
                </Detail>
                <Detail label="Reminder" onEdit={() => onEdit("schedule")} data-id="element-1036" data-mp="2zl1blkkluqjj">
                  {reminder && reminder.id !== "none" ? `${reminder.label}, ${reminder.time}` : "Off"}
                </Detail>
              </dl>
            </section>
          </div>
        </motion.div>}
    </>;
}
function Detail({
  label,
  children,
  onEdit
}: {
  label: string;
  children: ReactNode;
  onEdit: () => void;
}) {
  return <div className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0" data-id="element-1037" data-mp="2gv1oh38brlir">
      <div className="min-w-0" data-id="element-1038" data-mp="3n9asq96viq4c">
        <dt className="text-xs font-semibold text-ink-2" data-id="element-1039" data-mp="3na2kta19i5pv">{label}</dt>
        <dd className="mt-0.5 text-[15px] font-semibold text-ink" data-id="element-1040" data-mp="3s8dunkrk4g83">{children}</dd>
      </div>
      <button type="button" onClick={onEdit} className="focus-ring shrink-0 rounded-md text-sm font-semibold text-ink-2 underline underline-offset-4 hover:text-ink" data-id="element-1041" data-mp="1gl212mmj8q7q">
        Change<span className="sr-only" data-id="element-1042" data-mp="16w0oz7j3ysz1"> {label.toLowerCase()}</span>
      </button>
    </div>;
}